import type { Image } from "@/api/client";
import { fetchImage } from "@/api/client";
import { useState, useEffect } from "react";
import { StatusBadge } from "./StatusBadge";
import { cn } from "@/lib/utils";
import { Loader2, ImageIcon, X, Download, ZoomIn } from "lucide-react";

interface ImageGalleryProps {
  images: Image[];
  className?: string;
}

function ImageThumbnail({ image }: { image: Image }) {
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (image.status === 'completed' && image.filePath) {
      setLoading(true);
      fetchImage(image.id)
        .then((url) => {
          setImageUrl(url);
          setLoading(false);
        })
        .catch(() => {
          setError(true);
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [image.id, image.status, image.filePath]);

  const handleDownload = async () => {
    if (!imageUrl) return;
    const a = document.createElement('a');
    a.href = imageUrl;
    a.download = `image-${image.id}.png`;
    a.click();
  };

  return (
    <>
      <div
        className={cn(
          "relative aspect-square rounded-xl overflow-hidden bg-muted",
          "border border-border transition-all duration-200",
          image.status === 'completed' && "cursor-pointer hover:shadow-lg hover:scale-[1.02]"
        )}
        onClick={() => image.status === 'completed' && setShowModal(true)}
      >
        {/* Loading state */}
        {(loading || image.status === 'generating') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
            <span className="text-xs text-muted-foreground">
              {image.status === 'generating' ? '生成中...' : '加载中...'}
            </span>
          </div>
        )}

        {/* Pending state */}
        {image.status === 'pending' && !loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-muted">
            <ImageIcon className="w-8 h-8 text-muted-foreground" />
            <span className="text-xs text-muted-foreground">等待生成</span>
          </div>
        )}

        {/* Error state */}
        {(error || image.status === 'failed') && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-destructive/10">
            <ImageIcon className="w-8 h-8 text-destructive" />
            <span className="text-xs text-destructive">生成失败</span>
          </div>
        )}

        {/* Image */}
        {imageUrl && !loading && image.status === 'completed' && (
          <>
            <img
              src={imageUrl}
              alt={image.description || 'Generated image'}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-black/0 hover:bg-black/30 transition-colors flex items-center justify-center opacity-0 hover:opacity-100">
              <ZoomIn className="w-8 h-8 text-white" />
            </div>
          </>
        )}

        {/* Status badge */}
        <div className="absolute top-2 right-2">
          <StatusBadge status={image.status} />
        </div>
      </div>

      {/* Description */}
      {image.description && (
        <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
          {image.description}
        </p>
      )}

      {/* Full-screen modal */}
      {showModal && imageUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setShowModal(false)}
        >
          <button
            className="absolute top-4 right-4 p-2 text-white/80 hover:text-white rounded-full bg-white/10"
            onClick={() => setShowModal(false)}
          >
            <X className="w-6 h-6" />
          </button>
          
          <button
            className="absolute top-4 right-16 p-2 text-white/80 hover:text-white rounded-full bg-white/10"
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
          >
            <Download className="w-6 h-6" />
          </button>

          <img
            src={imageUrl}
            alt={image.description || 'Generated image'}
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
          
          {image.description && (
            <div className="absolute bottom-4 left-4 right-4 text-center">
              <p className="text-white/80 text-sm bg-black/50 rounded-lg px-4 py-2 inline-block max-w-2xl">
                {image.description}
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export function ImageGallery({ images, className }: ImageGalleryProps) {
  if (images.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <ImageIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>暂无图片</p>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4",
        className
      )}
    >
      {images.map((image) => (
        <div key={image.id}>
          <ImageThumbnail image={image} />
        </div>
      ))}
    </div>
  );
}
